import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, CheckCircle, CreditCard, DollarSign, Download, Edit3, Plus, RefreshCw, Save, Search, Trash2, X, XCircle } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import { clientService, invoiceService } from '../services/api';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { exportCsv } from '../utils/csv';

const emptyForm = {
  client_id: '',
  invoice_number: '',
  description: '',
  reference_month: '',
  amount: '',
  status: 'open',
  due_date: '',
  paid_date: '',
  payment_method: '',
  notes: '',
};

const statusLabels = {
  open: 'Em aberto',
  paid: 'Pago',
  overdue: 'Vencido',
  cancelled: 'Cancelado',
};

const statusClass = {
  open: 'badge-info',
  paid: 'badge-success',
  overdue: 'badge-warning',
  cancelled: 'badge-danger',
};

const paymentMethods = [
  'Pix',
  'Boleto',
  'Cartao',
  'Dinheiro',
  'Transferencia',
  'Manual',
];

const currency = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const dateLabel = (value) => {
  if (!value) return '-';
  return format(new Date(`${value}T00:00:00`), 'dd/MM/yyyy');
};

const monthLabel = (value) => {
  if (!value) return '-';
  const [year, month] = value.split('-');
  return `${month}/${year}`;
};

const toForm = (invoice) => ({
  client_id: invoice.client_id || '',
  invoice_number: invoice.invoice_number || '',
  description: invoice.description || '',
  reference_month: invoice.reference_month || '',
  amount: invoice.amount || '',
  status: invoice.status || 'open',
  due_date: invoice.due_date || '',
  paid_date: invoice.paid_date || '',
  payment_method: invoice.payment_method || '',
  notes: invoice.notes || '',
});

const Billing = () => {
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);

    try {
      const [clientResponse, invoiceResponse] = await Promise.all([
        clientService.getAll(),
        invoiceService.getAll(),
      ]);
      setClients(clientResponse.data.data || []);
      setInvoices(invoiceResponse.data.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao carregar financeiro');
    } finally {
      setLoading(false);
    }
  };

  const filteredInvoices = useMemo(() => {
    const term = search.trim().toLowerCase();

    return invoices.filter((invoice) => {
      const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
      const matchesSearch = !term || [
        invoice.invoice_number,
        invoice.description,
        invoice.reference_month,
        invoice.client?.name,
        invoice.client?.document,
        invoice.payment_method,
      ].filter(Boolean).some((value) => String(value).toLowerCase().includes(term));

      return matchesStatus && matchesSearch;
    });
  }, [invoices, search, statusFilter]);

  const openTotal = invoices
    .filter((invoice) => invoice.status === 'open')
    .reduce((sum, invoice) => sum + Number(invoice.amount || 0), 0);
  const overdueTotal = invoices
    .filter((invoice) => invoice.status === 'overdue')
    .reduce((sum, invoice) => sum + Number(invoice.amount || 0), 0);
  const paidTotal = invoices
    .filter((invoice) => invoice.status === 'paid')
    .reduce((sum, invoice) => sum + Number(invoice.amount || 0), 0);
  const openCount = invoices.filter((invoice) => invoice.status === 'open').length;

  const openCreateModal = () => {
    setEditingInvoice(null);
    setFormData({
      ...emptyForm,
      reference_month: new Date().toISOString().slice(0, 7),
      due_date: new Date().toISOString().slice(0, 10),
    });
    setModalOpen(true);
  };

  const openEditModal = (invoice) => {
    setEditingInvoice(invoice);
    setFormData(toForm(invoice));
    setModalOpen(true);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: value,
      ...(name === 'status' && value !== 'paid' ? { paid_date: '' } : {}),
    }));
  };

  const buildPayload = () => ({
    ...formData,
    amount: formData.amount === '' ? 0 : Number(formData.amount),
    invoice_number: formData.invoice_number || undefined,
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      const payload = buildPayload();

      if (editingInvoice) {
        const response = await invoiceService.update(editingInvoice.id, payload);
        setInvoices((current) => current.map((invoice) => (
          invoice.id === editingInvoice.id ? response.data.data : invoice
        )));
        toast.success('Cobranca atualizada com sucesso');
      } else {
        const response = await invoiceService.create(payload);
        setInvoices((current) => [response.data.data, ...current]);
        toast.success('Cobranca criada com sucesso');
      }

      setModalOpen(false);
      setEditingInvoice(null);
      setFormData(emptyForm);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar cobranca');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkPaid = async (invoice) => {
    try {
      const response = await invoiceService.markPaid(invoice.id, {
        paid_date: new Date().toISOString().slice(0, 10),
        payment_method: invoice.payment_method || 'Manual',
      });
      setInvoices((current) => current.map((item) => (
        item.id === invoice.id ? response.data.data : item
      )));
      toast.success('Cobranca marcada como paga');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao baixar cobranca');
    }
  };

  const handleDelete = async (invoice) => {
    const confirmed = window.confirm(`Remover a cobranca ${invoice.invoice_number}?`);
    if (!confirmed) return;

    try {
      await invoiceService.delete(invoice.id);
      setInvoices((current) => current.filter((item) => item.id !== invoice.id));
      toast.success('Cobranca removida');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao remover cobranca');
    }
  };

  const handleExport = () => {
    exportCsv('rcrastro-cobrancas.csv', [
      { key: 'invoice_number', label: 'Numero' },
      { key: 'client', label: 'Cliente' },
      { key: 'description', label: 'Descricao' },
      { key: 'reference_month', label: 'Referencia' },
      { key: 'amount', label: 'Valor' },
      { key: 'status', label: 'Status' },
      { key: 'due_date', label: 'Vencimento' },
      { key: 'paid_date', label: 'Pagamento' },
      { key: 'payment_method', label: 'Forma' },
    ], filteredInvoices.map((invoice) => ({
      invoice_number: invoice.invoice_number,
      client: invoice.client?.name || '',
      description: invoice.description,
      reference_month: monthLabel(invoice.reference_month),
      amount: currency.format(Number(invoice.amount || 0)),
      status: statusLabels[invoice.status] || invoice.status,
      due_date: dateLabel(invoice.due_date),
      paid_date: dateLabel(invoice.paid_date),
      payment_method: invoice.payment_method || '',
    })));
    toast.success('CSV de cobrancas gerado');
  };

  return (
    <div className="flex h-screen bg-dark-950">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Financeiro</h1>
              <p className="text-dark-400">Controle cobrancas, vencimentos e pagamentos dos clientes.</p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={handleExport}
                className="btn-secondary flex items-center justify-center gap-2"
              >
                <Download className="h-4 w-4" />
                Exportar CSV
              </button>
              <button
                type="button"
                onClick={loadData}
                className="btn-secondary flex items-center justify-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Atualizar
              </button>
              <button
                type="button"
                onClick={openCreateModal}
                className="btn-primary flex items-center justify-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Nova cobranca
              </button>
            </div>
          </div>

          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="card">
              <div className="mb-2 flex items-center gap-2 text-dark-400">
                <CreditCard className="h-4 w-4" />
                Em aberto
              </div>
              <p className="text-3xl font-bold text-primary-400">{currency.format(openTotal)}</p>
              <p className="mt-1 text-xs text-dark-500">{openCount} cobrancas</p>
            </div>
            <div className="card">
              <div className="mb-2 flex items-center gap-2 text-dark-400">
                <XCircle className="h-4 w-4" />
                Vencido
              </div>
              <p className="text-3xl font-bold text-warning-400">{currency.format(overdueTotal)}</p>
            </div>
            <div className="card">
              <div className="mb-2 flex items-center gap-2 text-dark-400">
                <CheckCircle className="h-4 w-4" />
                Recebido
              </div>
              <p className="text-3xl font-bold text-success-400">{currency.format(paidTotal)}</p>
            </div>
            <div className="card">
              <div className="mb-2 flex items-center gap-2 text-dark-400">
                <DollarSign className="h-4 w-4" />
                Total
              </div>
              <p className="text-3xl font-bold text-white">{invoices.length}</p>
            </div>
          </div>

          <div className="card mb-6">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_220px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-dark-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="input-field pl-11"
                  placeholder="Buscar por numero, cliente, descricao ou forma de pagamento"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="input-field"
              >
                <option value="all">Todos os status</option>
                {Object.entries(statusLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="card p-0">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1050px] text-left">
                <thead className="border-b border-dark-700 text-sm text-dark-400">
                  <tr>
                    <th className="px-5 py-4 font-medium">Cobranca</th>
                    <th className="px-5 py-4 font-medium">Cliente</th>
                    <th className="px-5 py-4 font-medium">Valor</th>
                    <th className="px-5 py-4 font-medium">Datas</th>
                    <th className="px-5 py-4 font-medium">Status</th>
                    <th className="px-5 py-4 text-right font-medium">Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="px-5 py-10 text-center text-dark-400">
                        Carregando cobrancas...
                      </td>
                    </tr>
                  ) : filteredInvoices.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-5 py-10 text-center text-dark-400">
                        <CreditCard className="mx-auto mb-2 h-10 w-10 opacity-50" />
                        Nenhuma cobranca encontrada
                      </td>
                    </tr>
                  ) : (
                    filteredInvoices.map((invoice) => (
                      <tr key={invoice.id} className="border-b border-dark-800 text-sm last:border-0">
                        <td className="px-5 py-4">
                          <div className="font-semibold text-white">{invoice.invoice_number}</div>
                          <div className="text-dark-400">{invoice.description}</div>
                          <div className="text-xs text-dark-500">Referencia {monthLabel(invoice.reference_month)}</div>
                        </td>
                        <td className="px-5 py-4 text-dark-300">
                          <div className="font-medium text-white">{invoice.client?.name || 'Cliente removido'}</div>
                          <div className="text-xs text-dark-500">{invoice.client?.document || ''}</div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="font-semibold text-white">{currency.format(Number(invoice.amount || 0))}</div>
                          <div className="text-xs text-dark-500">{invoice.payment_method || 'Sem forma informada'}</div>
                        </td>
                        <td className="px-5 py-4 text-dark-300">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-dark-500" />
                            Vence {dateLabel(invoice.due_date)}
                          </div>
                          <div className="text-xs text-dark-500">Pago {dateLabel(invoice.paid_date)}</div>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`badge ${statusClass[invoice.status] || 'badge-info'}`}>
                            {statusLabels[invoice.status] || invoice.status}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex justify-end gap-2">
                            {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                              <button
                                type="button"
                                onClick={() => handleMarkPaid(invoice)}
                                className="rounded-lg p-2 text-success-400 transition-colors hover:bg-dark-700"
                                title="Marcar como paga"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => openEditModal(invoice)}
                              className="rounded-lg p-2 text-primary-400 transition-colors hover:bg-dark-700"
                              title="Editar"
                            >
                              <Edit3 className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(invoice)}
                              className="rounded-lg p-2 text-danger-400 transition-colors hover:bg-dark-700"
                              title="Remover"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg border border-dark-700 bg-dark-900 shadow-2xl">
            <div className="flex items-center justify-between border-b border-dark-800 p-5">
              <div>
                <h2 className="text-lg font-bold text-white">
                  {editingInvoice ? 'Editar cobranca' : 'Nova cobranca'}
                </h2>
                <p className="text-sm text-dark-400">Lance mensalidades, vencimentos e pagamentos.</p>
              </div>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="rounded-lg p-2 text-dark-400 transition-colors hover:bg-dark-800 hover:text-white"
                title="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 p-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Cliente</label>
                  <select
                    name="client_id"
                    value={formData.client_id}
                    onChange={handleChange}
                    className="input-field"
                    required
                  >
                    <option value="">Selecionar cliente</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Numero</label>
                  <input
                    name="invoice_number"
                    value={formData.invoice_number}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="Gerado automaticamente se vazio"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-dark-300">Descricao</label>
                  <input
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="Mensalidade, adesao, taxa extra..."
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Referencia</label>
                  <input
                    type="month"
                    name="reference_month"
                    value={formData.reference_month}
                    onChange={handleChange}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Valor</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="149.90"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Vencimento</label>
                  <input
                    type="date"
                    name="due_date"
                    value={formData.due_date}
                    onChange={handleChange}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="input-field"
                  >
                    {Object.entries(statusLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Data de pagamento</label>
                  <input
                    type="date"
                    name="paid_date"
                    value={formData.paid_date}
                    onChange={handleChange}
                    className="input-field"
                    disabled={formData.status !== 'paid'}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Forma de pagamento</label>
                  <select
                    name="payment_method"
                    value={formData.payment_method}
                    onChange={handleChange}
                    className="input-field"
                  >
                    <option value="">Selecionar</option>
                    {paymentMethods.map((method) => (
                      <option key={method} value={method}>{method}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-dark-300">Observacoes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="input-field min-h-24"
                  placeholder="Informacoes internas da cobranca"
                />
              </div>

              <div className="flex flex-col-reverse gap-3 border-t border-dark-800 pt-5 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary flex items-center justify-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  {saving ? 'Salvando...' : 'Salvar cobranca'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;
